import type React from 'react'
import { useEffect, useState } from 'react'

import * as DialogPrimitive from '@radix-ui/react-dialog'

import { cn } from '@/lib/utils'

interface ImageViewerProps {
  imageUrl: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  initialRect?: DOMRect | null
}

export const ImageViewer: React.FC<ImageViewerProps> = ({ imageUrl, open, onOpenChange, initialRect }) => {
  const [isAnimating, setIsAnimating] = useState(false)
  const [imageStyle, setImageStyle] = useState<React.CSSProperties>({})

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onOpenChange(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, onOpenChange])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined

    if (open && initialRect) {
      // Calculate the initial position and scale
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const centerX = viewportWidth / 2
      const centerY = viewportHeight / 2

      // Calculate translation from center to initial position
      const initialX = initialRect.left + initialRect.width / 2 - centerX
      const initialY = initialRect.top + initialRect.height / 2 - centerY

      // Calculate scale based on thumbnail size vs final size (90% of viewport)
      const finalWidth = viewportWidth * 0.9
      const finalHeight = viewportHeight * 0.9
      const scaleX = initialRect.width / finalWidth
      const scaleY = initialRect.height / finalHeight
      const scale = Math.max(scaleX, scaleY)

      // Set initial position
      setImageStyle({
        transform: `translate(${initialX}px, ${initialY}px) scale(${scale})`,
        transition: 'none',
      })

      // Trigger animation to final position
      requestAnimationFrame(() => {
        setIsAnimating(true)
        setImageStyle({
          transform: 'translate(0, 0) scale(1)',
          transition: 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)',
        })
      })
    } else if (!open) {
      // Reset for close animation
      if (initialRect && isAnimating) {
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const centerX = viewportWidth / 2
        const centerY = viewportHeight / 2

        const initialX = initialRect.left + initialRect.width / 2 - centerX
        const initialY = initialRect.top + initialRect.height / 2 - centerY

        const finalWidth = viewportWidth * 0.9
        const finalHeight = viewportHeight * 0.9
        const scaleX = initialRect.width / finalWidth
        const scaleY = initialRect.height / finalHeight
        const scale = Math.max(scaleX, scaleY)

        setImageStyle({
          transform: `translate(${initialX}px, ${initialY}px) scale(${scale})`,
          transition: 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)',
        })
      }
      timeoutId = setTimeout(() => {
        setIsAnimating(false)
      }, 300)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [open, initialRect, isAnimating])

  if (!imageUrl) return null

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/90 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed top-[50%] left-[50%] z-50',
            'translate-x-[-50%] translate-y-[-50%]',
            'h-[90vh] w-[90vw]',
            'flex items-center justify-center',
            'outline-none'
          )}
          onClick={() => {
            onOpenChange(false)
          }}>
          <img
            src={imageUrl}
            alt="Full size image"
            className="max-h-full max-w-full object-contain"
            style={initialRect ? imageStyle : undefined}
            onClick={(e) => {
              e.stopPropagation()
            }}
          />
          <DialogPrimitive.Close
            className={cn(
              'absolute top-4 right-4 rounded-sm opacity-0 transition-all hover:opacity-100 focus:outline-none disabled:pointer-events-none',
              isAnimating && 'opacity-70 delay-200'
            )}>
            <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
